import React, { useCallback, useEffect, useState } from "react";

import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import { Box, Button, CardMedia, Stack, Switch, TextareaAutosize, TextField, Typography } from "@mui/material";
import { HelpIcon } from "assets/material-custom-icons";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormikContext } from "formik";
import pickFiles from "utils/pick-files";

import { IFormikValues } from "../EditWindow";

import "./UserDetails.styles.scss";

export const UserDetails: React.FC = () => {
  const { values, setFieldValue, errors } = useFormikContext<IFormikValues>();
  const [avatarSrc, setAvatarSrc] = useState<string>(values.details.profile_photo_url);

  useEffect(() => {
    setAvatarSrc(values.details.profile_photo_url);
  }, [values.details.profile_photo_url]);

  const updateAvatarHandler = async () => {
    let file: File | null = null;

    const fileList = await pickFiles();
    if (fileList?.length) {
      file = fileList[0];
      const blobUrl = URL.createObjectURL(file);
      setAvatarSrc(blobUrl);
      setFieldValue("details.photo", fileList[0]);
    } else return; //if user clicked cancel don't send any request
    return file;
  };

  const handleRemoveAvatar = useCallback(() => {
    setAvatarSrc("");
    setFieldValue("details.photo", null);
  }, []);

  return (
    <div style={{ width: "100%", borderRadius: "8px", backgroundColor: "#fff" }}>
      <Box sx={{ padding: "40px 32px" }}>
        <Stack direction={"column"} sx={{ gap: "25px", marginBottom: "40px" }}>
          <TextField
            required
            error={!!errors.details?.first_name}
            helperText={errors.details?.first_name}
            sx={{
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "#5d7782"
              },
              "& .MuiFormHelperText-root": {
                bottom: "-19px",
                position: "absolute",
                margin: "0",
                color: "#d32f2f"
              }
            }}
            onChange={(e) => {
              if (e.target.value.length > 249) return;
              setFieldValue("details.first_name", e.target.value);
            }}
            value={values.details.first_name}
            label="First Name"
            placeholder="First Name"
            variant="outlined"
          />
          <TextField
            required
            error={!!errors.details?.last_name}
            helperText={errors.details?.last_name}
            sx={{
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "#5d7782"
              },
              "& .MuiFormHelperText-root": {
                bottom: "-19px",
                position: "absolute",
                margin: "0",
                color: "#d32f2f"
              }
            }}
            onChange={(e) => {
              if (e.target.value.length > 249) return;
              setFieldValue("details.last_name", e.target.value);
            }}
            value={values.details.last_name}
            label="Last Name"
            variant="outlined"
          />
          <TextField
            sx={{
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "#5d7782"
              }
            }}
            onChange={(e) => {
              if (e.target.value.length > 99) return;
              setFieldValue("details.position", e.target.value);
            }}
            value={values.details.position}
            label="Position"
            variant="outlined"
          />
          <TextField
            required
            error={!!errors.details?.company_name}
            helperText={errors.details?.company_name}
            sx={{
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "#5d7782"
              },
              "& .MuiFormHelperText-root": {
                bottom: "-19px",
                position: "absolute",
                margin: "0",
                color: "#d32f2f"
              }
            }}
            onChange={(e) => {
              if (e.target.value.length > 249) return;
              setFieldValue("details.company_name", e.target.value);
            }}
            value={values.details.company_name}
            label="Company"
            variant="outlined"
          />
          <TextField
            sx={{
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "#5d7782"
              }
            }}
            onChange={(e) => {
              if (e.target.value.length > 249) return;
              setFieldValue("details.location", e.target.value);
            }}
            value={values.details.location}
            label="Location"
            variant="outlined"
          />
          <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
            <Typography
              sx={{
                padding: "0 4px",
                top: "-9px",
                left: "10px",
                margin: "0",
                fontWeight: "400",
                color: "#5d7782",
                fontSize: "0.75em",
                position: "absolute",
                backgroundColor: "#fff"
              }}
            >
              About me
            </Typography>
            <TextareaAutosize
              className="about-area"
              placeholder="Add a short bio (role, focus areas, and interests)"
              onChange={(e) => {
                if (e.target.value.length > 499) return;
                setFieldValue("details.about", e.target.value);
              }}
              value={values.details.about || ""}
            />
          </Box>
          <Stack display="flex" flexDirection="row" alignItems="center" justifyContent="flex-start" gap="5px">
            <Switch
              focusVisibleClassName=".Mui-focusVisible"
              disableRipple
              checked={!values.details.show_social_profile}
              onChange={() => setFieldValue("details.show_social_profile", !values.details.show_social_profile)}
              sx={{
                width: 48,
                height: 24,
                padding: 0,
                "& .MuiSwitch-switchBase": {
                  padding: 0,
                  margin: "2px",
                  transitionDuration: "300ms",
                  color: "#bac7ce",
                  "&.Mui-checked": {
                    transform: "translateX(23px)",
                    color: "#0a3142",
                    "& + .MuiSwitch-track": {
                      backgroundColor: "#dae0e2",
                      opacity: 1,
                      border: 0
                    },
                    "&.Mui-disabled + .MuiSwitch-track": {
                      opacity: 0.5
                    }
                  },
                  "&.Mui-focusVisible .MuiSwitch-thumb": {
                    color: "#33cf4d",
                    border: "6px solid #fff"
                  },
                  "&.Mui-disabled .MuiSwitch-thumb": {
                    opacity: 0.7
                  },
                  "&.Mui-disabled + .MuiSwitch-track": {
                    opacity: 0.3
                  }
                },
                "& .MuiSwitch-thumb": {
                  boxSizing: "border-box",
                  width: 20,
                  height: 20
                },
                "& .MuiSwitch-track": {
                  borderRadius: "34px",
                  backgroundColor: "#dae0e2",
                  opacity: 1,
                  transition: "background-color 500ms"
                }
              }}
            />
            <Typography color="#0a3142" marginRight="9px">
              Make profile private
            </Typography>
            <MaterialTooltip isShow title="Your profile will be invisible for other users">
              <HelpIcon
                sx={{
                  fill: "#0a3142",
                  "&:hover": {
                    cursor: "pointer"
                  }
                }}
              />
            </MaterialTooltip>
          </Stack>
        </Stack>
        <Typography sx={{ marginBottom: "8px", color: "#5d7782" }}>Your photo</Typography>
        {avatarSrc ? (
          <Box sx={{ position: "relative", width: "fit-content", height: "fit-content" }}>
            <CancelRoundedIcon
              sx={{ color: "#5d7782", cursor: "pointer", position: "absolute", top: "10px", right: "10px" }}
              onClick={handleRemoveAvatar}
            />
            <CardMedia
              component="img"
              height="258px"
              sx={{ minWidth: "203px", maxWidth: "203px", borderRadius: "8px", bgcolor: "#0a3142" }}
              src={avatarSrc}
            />
          </Box>
        ) : (
          <Button onClick={updateAvatarHandler} variant="outlined">
            {" "}
            Add Photo
          </Button>
        )}
      </Box>
    </div>
  );
};
